import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'sonner';

import type { PaymentActionRequiredMeta } from '@/types/customer-plan-management';
import type { CustomerCheckoutStripeConfig } from '@/types/customer-subscription-checkout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useCustomerSubscriptionCheckoutContext } from '@/hooks/customer/useCustomerSubscriptionCheckout';
import { extractJsonApiErrorMessage } from '@/lib/jsonapi/errors';
import { isStripeReady, resolveStripeAccountOption } from '@/lib/stripe/isStripeReady';

interface PaymentActionRequiredDialogProps {
  meta: PaymentActionRequiredMeta | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmed: () => Promise<void> | void;
}

export function PaymentActionRequiredDialog({
  meta,
  open,
  onOpenChange,
  onConfirmed,
}: PaymentActionRequiredDialogProps) {
  const { t } = useTranslation();
  const checkoutContextQuery = useCustomerSubscriptionCheckoutContext(undefined, open);
  const [isConfirming, setIsConfirming] = useState(false);

  const clientSecret = meta?.client_secret?.trim() ?? '';

  const stripeConfig: CustomerCheckoutStripeConfig | null = (() => {
    const candidate = checkoutContextQuery.data?.stripe ?? null;
    if (candidate?.publishableKey) return candidate;

    const envKey = typeof import.meta.env.VITE_STRIPE_PUBLIC_KEY === 'string' ? import.meta.env.VITE_STRIPE_PUBLIC_KEY : '';
    const normalizedEnvKey = envKey.trim();
    if (!normalizedEnvKey) return candidate;

    return {
      publishableKey: normalizedEnvKey,
      accountId: null,
      accountType: null,
    };
  })();

  const publishableKey = stripeConfig?.publishableKey?.trim() ?? '';
  const stripeAccountOption = resolveStripeAccountOption(stripeConfig);

  const stripePromise = useMemo(() => {
    if (!isStripeReady(stripeConfig)) return null;
    return loadStripe(publishableKey, stripeAccountOption);
  }, [publishableKey, stripeAccountOption?.stripeAccount, stripeConfig?.accountType]);

  const canConfirm = Boolean(clientSecret) && Boolean(stripePromise) && !checkoutContextQuery.isLoading;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('customer.planManagement.paymentAction.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('customer.planManagement.paymentAction.description')}
          </AlertDialogDescription>
          {checkoutContextQuery.isError ? (
            <AlertDialogDescription className="text-destructive">
              {extractJsonApiErrorMessage(
                checkoutContextQuery.error,
                t('customer.planManagement.paymentAction.contextError'),
              )}
            </AlertDialogDescription>
          ) : null}
          {!publishableKey ? (
            <AlertDialogDescription className="text-destructive">
              {t('customer.planManagement.paymentAction.missingStripe')}
            </AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isConfirming}>
            {t('customer.planManagement.paymentAction.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              onClick={async () => {
                if (!clientSecret) {
                  toast.error(t('customer.planManagement.paymentAction.missingClientSecret'));
                  return;
                }

                const stripe = await stripePromise;
                if (!stripe) {
                  toast.error(t('customer.planManagement.paymentAction.missingStripe'));
                  return;
                }

                try {
                  setIsConfirming(true);
                  const result = await stripe.confirmCardPayment(clientSecret);
                  if (result.error) {
                    toast.error(result.error.message ?? t('customer.planManagement.paymentAction.failed'));
                    return;
                  }

                  const status = result.paymentIntent?.status;
                  if (status && status !== 'succeeded' && status !== 'processing') {
                    toast.error(t('customer.planManagement.paymentAction.failed'));
                    return;
                  }

                  await onConfirmed();
                  toast.success(t('customer.planManagement.paymentAction.success'));
                  onOpenChange(false);
                } catch (error) {
                  toast.error(extractJsonApiErrorMessage(error, t('customer.planManagement.paymentAction.failed')));
                } finally {
                  setIsConfirming(false);
                }
              }}
              disabled={!canConfirm || isConfirming}
            >
              {isConfirming
                ? t('customer.planManagement.paymentAction.processing')
                : t('customer.planManagement.paymentAction.confirm')}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
